import React from 'react';
import PropTypes from 'prop-types';
import { isEqual, map, isEmpty, find, cloneDeep, filter } from 'lodash';
import { Bert } from 'meteor/themeteorchef:bert';
import { Meteor } from 'meteor/meteor';
import { withTracker } from 'meteor/react-meteor-data';

import { Box, Menu, MenuItem, Typography, Stack } from '@mui/material';
import SwitchVideoIcon from '@mui/icons-material/SwitchVideo';
import ListIcon from '@mui/icons-material/List';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Loading from '../Loading';

import {
  renderTable,
  renderIconButton,
  renderSwitch,
  renderRadioButton,
  renderTextField
} from '../MaterialMUIComponent';
import * as videoOps from '../../../api/Videos/video-operations';
import * as templateOps from '../../../api/AnnotationTemplates/annotation-template-operations';
import * as configOps from '../../../api/ConfigurationsConnectors/configurations-connectors-operations';
import * as sourceOps from '../../../api/Sources/source-operations';

import ConfigurationsConnectors from '../../../api/ConfigurationsConnectors/ConfigurationsConnectors';
import AnnotationTemplates, {
  LIBRARY_ICON_TYPES
} from '../../../api/AnnotationTemplates/AnnotationTemplates';
import AnnotationTemplatePreview from '../AnnotationTemplatePreview/index';
import ConnectionDataBase from '../ConnectionDataBase/ConnectionDataBase';

import handleErrorInClient from '../../../modules/handle-error-in-client';
import methodPromise from '../../../modules/method-promise-wrapper';

const AnnotationTemplateSettings = ({
  setClose,
  envSettings,
  videos,
  annotationTemplatesCustom,
  configConnectorsList
}) => {
  const [viewConfigConnectors, setViewConfigConnectors] = React.useState(true);
  const [viewDataBase, setViewDatabase] = React.useState(true);
  const [isBaseTemplate, setIsBaseTemplate] = React.useState(true);

  const [videoSelected, setVideoSelected] = React.useState(videos[0]);
  const [anchorElVideos, setAnchorElVideos] = React.useState({ anchor: null, top: 0, left: 0 });

  const templatesCustomToView = filter(annotationTemplatesCustom, item =>
    videoOps.getAnnotationTemplatesCustom(videoSelected || {}).includes(templateOps.getId(item))
  );
  const configsConnectorsToView = filter(configConnectorsList, item =>
    isEqual(videoOps.getId(videoSelected || {}), configOps.getVideoId(item))
  );
  const annotationsTemplates = videoOps.getAnnotationTemplates(videoSelected);
  const templatesToRender = isBaseTemplate ? annotationsTemplates : templatesCustomToView;
  const [templateSelected, setTemplateSelected] = React.useState(annotationsTemplates[0]);

  const [sourceSelected, setSourceSelected] = React.useState({});
  const [sourceAttributes, setSourceAttributes] = React.useState([]);
  const [searchSource, setSearchSource] = React.useState('');
  const schemaConfigDefault = {
    name: '',
    videoId: videoOps.getId(videoSelected),
    templateId: templateOps.getId(templateSelected),
    connectorId: sourceOps.getConnectorId(sourceSelected),
    source: sourceOps.getLayerName(sourceSelected),
    isOnlyGeometry: false,
    connectionsFields: map(templateOps.getElements(templateSelected), item => {
      return {
        nameField: 'none',
        value: 'none',
        elementIndex: templateOps.getElementOrder(item),
        elementAsociatedIndex: -1
      };
    })
  };
  const [configSelected, setConfigSelected] = React.useState(schemaConfigDefault);

  const [loadingSaving, setLoadingSaving] = React.useState(false);

  const updateAnnotationFields = (elementItem, property, value) => {
    const cloneSelectedTemplate = cloneDeep(templateSelected);
    const elementUpdate = find(templateOps.getElements(templateSelected), templateEl =>
      templateOps.getAreEqualElements(templateEl, elementItem)
    );
    const indexElement = templateOps
      .getElements(templateSelected)
      .indexOf(
        find(templateOps.getElements(templateSelected), templateEl =>
          templateOps.getAreEqualElements(templateEl, elementItem)
        )
      );
    if (!isEmpty(elementUpdate) && !isEqual(indexElement, -1)) {
      const newElement = { ...elementUpdate, [property]: value };
      cloneSelectedTemplate.elements[indexElement] = newElement;
      setTemplateSelected(cloneSelectedTemplate);
    }
  };

  const updateFieldsConnector = (field, orderElement, isSourceField) => {
    const newConnectionFields = map(configOps.getConnectionsFields(configSelected), item => {
      const newItem = cloneDeep(item);
      if (isEqual(configOps.getElementIndex(newItem), orderElement)) {
        newItem.nameField = !isSourceField ? newItem.nameField : field;
        newItem.value = !isSourceField ? field : newItem.value;
      }
      return newItem;
    });
    setConfigSelected({ ...configSelected, connectionsFields: newConnectionFields });
  };

  const handleConfigSelect = newOption => {
    const newConfigSelect = find(configsConnectorsToView, item =>
      isEqual(configOps.getId(item), newOption.uniqueValue)
    );

    if (!isEmpty(configOps.getSource(newConfigSelect || {})))
      setSearchSource(configOps.getSource(newConfigSelect));

    const newTemplate = find(templatesCustomToView, item =>
      isEqual(templateOps.getId(item), configOps.getTemplateId(newConfigSelect || {}))
    );
    if (!isEmpty(newTemplate)) {
      setTemplateSelected(newTemplate);
      setIsBaseTemplate(false);
    }
    setConfigSelected(
      !isEqual(newOption.uniqueValue, 'new') ? newConfigSelect : schemaConfigDefault
    );
  };

  const handleSubmit = async () => {
    const templateToSave = {
      ...templateSelected,
      isBase: false,
      templateBaseId: templateSelected._id
    };
    if (isBaseTemplate) delete templateToSave._id;
    let templateCustomId = '';
    let newConfigConnectorId = '';
    try {
      const methodCallConfig =
        isEmpty(configOps.getId(configSelected)) && isBaseTemplate
          ? 'configurationsConnectors.insert'
          : 'configurationsConnectors.update';
      const methodCallTemplates = isBaseTemplate
        ? 'annotationTemplates.insert'
        : 'annotationTemplates.update';
      templateCustomId = await methodPromise(methodCallTemplates, templateToSave);
      if (isBaseTemplate) {
        const videoUpdated = {
          ...videoSelected,
          templatesId: !isEmpty(videoSelected.templatesId)
            ? [...videoSelected.templatesId, templateCustomId]
            : [templateCustomId]
        };
        await methodPromise('videos.update', videoUpdated);
      }
      const configToSave = {
        ...configSelected,
        templateId: templateCustomId
      };
      newConfigConnectorId = await methodPromise(methodCallConfig, configToSave);
      templateToSave._id = templateCustomId;
      setTemplateSelected(templateToSave);
      setIsBaseTemplate(false);
      Bert.alert('Configuration successfully saved', 'success');
    } catch (error) {
      if (!isEmpty(templateCustomId))
        try {
          await methodPromise('annotationTemplates.hardDelete', templateCustomId);
        } catch (errorRemoveVideo) {
          handleErrorInClient(errorRemoveVideo);
          Bert.alert(
            'Error removing settings template in error saving new configuration',
            'danger'
          );
        }
      if (!isEmpty(newConfigConnectorId))
        try {
          await methodPromise('configurationsConnectors.hardDelete', newConfigConnectorId);
        } catch (errorRemoveConfig) {
          handleErrorInClient(errorRemoveConfig);
          Bert.alert(
            'Error removing configuration connector in error saving new configuration',
            'danger'
          );
        }
      handleErrorInClient(error);
      Bert.alert('Error saving settings template', 'danger');
    }
    setLoadingSaving(false);
  };

  const deleteCustomTemplate = async () => {
    try {
      await methodPromise('annotationTemplates.hardDelete', templateOps.getId(templateSelected));
      const videoUpdated = {
        ...videoSelected,
        templatesId: filter(
          videoSelected.templatesId,
          id => !isEqual(templateOps.getId(templateSelected), id)
        )
      };
      await methodPromise('videos.update', videoUpdated);
      if (isEmpty(videoOps.getAnnotationTemplatesCustom(videoUpdated))) {
        setIsBaseTemplate(true);
        setTemplateSelected(videoOps.getAnnotationTemplates(videoUpdated)[0]);
      } else {
        setTemplateSelected(
          find(templatesCustomToView, item =>
            isEqual(templateOps.getId(item), videoUpdated.templatesId[0])
          )
        );
      }
      Bert.alert('Configuration successfully removed', 'success');
    } catch (error) {
      handleErrorInClient(error);
      Bert.alert('Error removing settings template', 'danger');
    }
  };

  React.useEffect(() => {
    const videoUpdate = find(videos, vid =>
      isEqual(videoOps.getId(vid), videoOps.getId(videoSelected))
    );
    if (isEmpty(videoOps.getAnnotationTemplatesCustom(videoUpdate)) && !isBaseTemplate) {
      setIsBaseTemplate(true);
    }
    setVideoSelected(videoUpdate);
  }, [videos]);

  const renderManyIconButtons = array =>
    map(
      array,
      (item, index) =>
        item.isShow &&
        renderIconButton({
          key: `iconButton-${item.title}-${index}`,
          onClick: item.function,
          icon: item.icon,
          titleTooltip: item.title,
          disabled: false,
          style: item.style || {}
        })
    );

  const renderIconTemplate = annotationTemplate => {
    const libraryIcons = templateOps.getLibraryIcon(annotationTemplate);
    if (isEqual(libraryIcons, LIBRARY_ICON_TYPES.FONTAWESOME))
      return <FontAwesomeIcon icon={templateOps.getIcon(annotationTemplate)} />;
    if (isEqual(libraryIcons, LIBRARY_ICON_TYPES.SVG))
      return (
        <img
          src={templateOps.getIconSvg(annotationTemplate)}
          alt={templateOps.getIcon(annotationTemplate)}
          style={{ marginRight: '5px', width: '20px', height: '20px' }}
        />
      );
    return <FontAwesomeIcon icon={templateOps.getIcon(annotationTemplate)} />;
  };

  const elementsColumnOneHeader = [
    <Stack key={`row1-column1-${videoOps.getId(videoSelected)}`} direction="row" width="100%">
      {renderManyIconButtons([
        {
          function: () => setViewConfigConnectors(!viewConfigConnectors),
          icon: <ListIcon />,
          title: viewConfigConnectors ? 'Hidden Configurations' : 'View Configurations',
          isShow: true
        },
        {
          function: () => setViewDatabase(!viewDataBase),
          icon: <FontAwesomeIcon icon="database" />,
          title: viewDataBase ? 'Hidden Data Base' : 'View Data Base',
          isShow: true
        },
        {
          function: event =>
            setAnchorElVideos({
              anchor: event.currentTarget,
              left: event.clientX,
              top: event.clientY
            }),
          icon: <SwitchVideoIcon style={{ color: '#2A3F90' }} />,
          title: 'Videos',
          isShow: true
        }
      ])}
      <Typography
        maxWidth="100%"
        minWidth="20%"
        marginLeft="1%"
        marginRight="2%"
        marginTop="0.5%"
        variant="h5"
      >
        {videoOps.getName(videoSelected)}
      </Typography>
      {viewDataBase && (
        <ConnectionDataBase
          key="connection-data-base"
          sourceSelected={sourceSelected}
          setSourceSelected={newSource => {
            setSourceSelected(newSource);
            setConfigSelected({
              ...configSelected,
              source: sourceOps.getId(newSource || {}),
              connectorId: sourceOps.getConnectorId(newSource || {})
            });
          }}
          isSourceAttributes
          setSourceAttributes={newAttributes => setSourceAttributes(newAttributes)}
          styleProp={{ width: '50%', marginRight: '2%' }}
          searchSource={searchSource}
        />
      )}
      {viewConfigConnectors &&
        renderTextField({
          key: 'nameConfig',
          label: 'Name configuration',
          style: { width: '100%', marginLeft: '1%' },
          moreProps: {
            value: configOps.getName(configSelected),
            onChange: event => setConfigSelected({ ...configSelected, name: event.target.value })
          }
        })}
    </Stack>,

    <Menu
      key={`menu-row2-column0-${videoOps.getId(videoSelected)}`}
      open={Boolean(anchorElVideos.anchor)}
      anchorEl={anchorElVideos.anchor}
      onClose={() => setAnchorElVideos({ ...anchorElVideos, anchor: null })}
      style={{
        left: `${anchorElVideos.left}px`,
        top: `${anchorElVideos.top}px`
      }}
    >
      {map(videos, element => {
        return (
          <MenuItem
            autoFocus={isEqual(videoSelected._id, element._id)}
            selected={isEqual(videoSelected._id, element._id)}
            key={element._id}
            onClick={() => {
              setVideoSelected(element);
              setTemplateSelected(videoOps.getAnnotationTemplates(element)[0]);
              setIsBaseTemplate(true);
              setConfigSelected({
                ...configSelected,
                videoId: videoOps.getId(element),
                templateId: templateOps.getId(videoOps.getAnnotationTemplates(element)[0])
              });
              setAnchorElVideos({ ...anchorElVideos, anchor: null });
            }}
          >{`${videoOps.getName(element)}`}</MenuItem>
        );
      })}
    </Menu>
  ];
  const elementsColumnTwoHeader = [
    <Stack
      key={`row1-column2-${videoOps.getId(videoSelected)}`}
      direction="row"
      display="flex"
      justifyContent="flex-end"
    >
      {renderSwitch({
        key: 'option-templates',
        checked: !isEmpty(templatesCustomToView) ? isBaseTemplate : true,
        disabled: isEmpty(templatesCustomToView),
        onChange: event => {
          setIsBaseTemplate(event.target.checked);
          if (isEqual(event.target.checked, false)) {
            setTemplateSelected(templatesCustomToView[0]);
            const config = find(configsConnectorsToView, item =>
              isEqual(configOps.getTemplateId(item), templateOps.getId(templatesCustomToView[0]))
            );
            setConfigSelected(config || schemaConfigDefault);
            setSearchSource(configOps.getSource(config || ''));
          } else {
            setTemplateSelected(annotationsTemplates[0]);
          }
        },
        labelCheck: 'View base templates',
        labelUncheck: 'View custom templates',
        styleLabel: { margin: 0 }
      })}

      {!loadingSaving &&
        renderManyIconButtons([
          {
            function: () => deleteCustomTemplate(),
            icon: <DeleteIcon style={{ color: 'red' }} />,
            isShow: !isBaseTemplate && !isEmpty(templatesCustomToView),
            title: 'Delete custom template'
          },
          {
            isShow: true,
            function: () => {
              setLoadingSaving(true);
              handleSubmit();
            },
            icon: <SaveIcon style={{ color: '#2A3F90' }} />,
            title: isBaseTemplate ? 'Save template configuration' : 'Update template configuration'
          },
          {
            isShow: true,
            function: () => setClose(false),
            icon: <CloseIcon style={{ color: 'red' }} />,
            title: 'Close'
          }
        ])}
      {loadingSaving && <Loading />}
    </Stack>
  ];
  const constructorTableHeader = [
    { row: 1, column: 1, maxWidthCol: '100%', elements: elementsColumnOneHeader },
    { row: 1, column: 2, maxWidthCol: '30%', elements: elementsColumnTwoHeader }
  ];
  const tableHeader = map(constructorTableHeader, item => {
    return {
      row: item.row,
      column: item.column,
      maxWidthCol: item.maxWidthCol,
      rowHeight: '5%',
      mbRow: '2%',
      rowJustifyContent: 'flex-start',
      showElement: true,
      elements: item.elements
    };
  });

  const buttonVideoTemplates = map(templatesToRender, template => {
    return {
      isShow: true,
      function: () => {
        setTemplateSelected(template);
        if (!isBaseTemplate) {
          const config = find(configsConnectorsToView, item =>
            isEqual(templateOps.getId(template), configOps.getTemplateId(item))
          );
          setConfigSelected(config || schemaConfigDefault);
        }
      },
      icon: renderIconTemplate(template || {}),
      title: templateOps.getName(template),
      style: {
        color: isEqual(templateOps.getId(templateSelected), templateOps.getId(template))
          ? 'green'
          : '#2A3F90',
        width: '38px',
        height: '38px',
        padding: 0,
        marginRight: '1%'
      }
    };
  });

  const elementsColOneContent = [
    viewConfigConnectors && (
      <Box
        key="colum1-box-listConfig-radioButton"
        sx={{
          maxHeight: '50vh',
          overflow: 'auto',
          marginTop: '1%',
          marginRight: '1%',
          borderRight: 1
        }}
      >
        {renderRadioButton({
          key: 'select-configuration',
          isRow: false,
          arrayLabels: [''].concat(
            map(configsConnectorsToView, config => {
              const templateConfig = find(
                videoOps.getAnnotationTemplates(videoSelected),
                videoTemplate =>
                  isEqual(templateOps.getId(videoTemplate), configOps.getTemplateId(config))
              );
              return renderIconTemplate(templateConfig || {});
            })
          ),
          isArrayObject: true,
          arrayOptions: [{ value: 'New...', uniqueValue: 'new' }].concat(
            map(configsConnectorsToView, config => {
              return {
                value: configOps.getName(config),
                uniqueValue: configOps.getId(config)
              };
            })
          ),
          onChange: newOption => handleConfigSelect(newOption),
          valueChecked: !isEmpty(configOps.getId(configSelected))
            ? configOps.getId(configSelected)
            : 'new'
        })}
      </Box>
    )
  ];

  const elementsColTwoContent = [
    <Stack key="row-buttons-templates" direction="row" display="flex" justifyContent="flex-start">
      {renderManyIconButtons(buttonVideoTemplates)}
    </Stack>,
    <Box
      key="box-annotation-preview"
      style={{ width: '100%', maxHeight: '45vh', overflow: 'auto' }}
    >
      <AnnotationTemplatePreview
        key="AnnotationPreview"
        annotationTemplate={templateSelected}
        updateAnnotationFields={updateAnnotationFields}
        previewMode="edition"
        isSettingsMode
        envSettings={envSettings}
        fieldsTableConenction={sourceAttributes}
        handleFieldMenu={updateFieldsConnector}
        fieldsMenusValues={configOps.getConnectionsFields(configSelected)}
      />
    </Box>
  ];
  const constructorTableContent = [
    {
      row: 1,
      column: 1,
      maxWidthCol: '20%',
      isShowCol: viewConfigConnectors,
      mrCol: '2%',
      elements: elementsColOneContent
    },
    {
      row: 1,
      column: 2,
      maxWidthCol: '100%',
      mrCol: '0%',
      isShowCol: true,
      elements: elementsColTwoContent
    }
  ];
  const tableContent = map(constructorTableContent, item => {
    return {
      row: item.row,
      column: item.column,
      rowJustifyContent: 'flex-start',
      maxWidthCol: item.maxWidthCol,
      showElement: true,
      isShowCol: item.isShowCol,
      mrCol: item.mrCol,
      elements: item.elements
    };
  });
  return (
    <Box width="95%" marginTop="2.5%" marginLeft={viewConfigConnectors ? '0%' : '5%'}>
      {renderTable(tableHeader)}
      {renderTable(tableContent)}
    </Box>
  );
};

AnnotationTemplateSettings.defaultProps = {
  annotationTemplatesCustom: [],
  configConnectorsList: []
};

AnnotationTemplateSettings.propTypes = {
  setClose: PropTypes.func.isRequired,
  envSettings: PropTypes.string.isRequired,
  videos: PropTypes.array.isRequired,
  annotationTemplatesCustom: PropTypes.array,
  configConnectorsList: PropTypes.array
};

export default withTracker(({ videos }) => {
  const configConnectorsSub = Meteor.subscribe(
    'configConnector.customTemplates.videos',
    map(videos, vid => vid._id)
  );
  const configConnectorsList = ConfigurationsConnectors.find({}, { sort: { name: 1 } }).fetch();
  const annotationTemplatesCustom = AnnotationTemplates.find(
    {},
    {
      sort: { name: 1 },
      fields: {
        createdAt: 0,
        updatedAt: 0
      }
    }
  ).fetch();
  return {
    loading: !configConnectorsSub.ready(),
    annotationTemplatesCustom,
    configConnectorsList
  };
})(AnnotationTemplateSettings);
